// src/components/dashboard/QuestionStatistics.tsx
// ✅ CORRIGIDO: AbortController + guards contra requisições em cascata

import React, { useState, useEffect, useRef } from 'react';
import {
    TrendingUp, TrendingDown, Minus, Target, Clock, AlertTriangle,
    ChevronDown, ChevronRight, Zap, BookOpen, BarChart2,
    Layers, RefreshCw, CheckCircle, XCircle, Activity,
} from 'lucide-react';
import './QuestionStatistics.css';

const API = '/api/question-stats';

const token = () => localStorage.getItem('accessToken');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

async function apiFetch(path: string, signal?: AbortSignal) {
    const res = await fetch(`${API}${path}`, { headers: authHeader(), signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

interface Trend { value: number; direction: 'up' | 'down' | 'neutral'; }
interface Overview {
    totalResolved: number;
    accuracy: number;
    firstAttemptPct: number;
    avgTimeFormatted: string;
    avgTimeWrongFormatted: string;
    avgTimeMs: number;
    trends: { total: Trend; accuracy: Trend; avgTime: Trend; };
}
interface DayData { date: string; label: string; total: number; correct: number; accuracy: number | null; avgTimeMs: number | null; }
interface DiffEntry { level: string; label: string; total: number; correct: number; accuracy: number; vsAverage: number; }
interface ErrorAnalysis {
    recurrentErrorCount: number;
    recurrentRate: number;
    periodErrorCount: number;
    errorRate: number;
    avgTimeWrongFormatted: string;
    topErrors: { questionId: string; wrongAttempts: number; attempts: number; }[];
}
interface PerfNode {
    name: string;
    level: number;
    total: number;
    correct: number;
    accuracy: number;
    avgTimeFormatted: string;
    byDiff: { level: string; label: string; total: number; accuracy: number; }[];
    children: PerfNode[];
}
interface FragilityItem {
    name: string;
    parent: string;
    errors: number;
    total: number;
    errorRate: number;
    fragilityScore: number;
}

type PeriodKey = '7' | '30' | '90' | 'all';
type TabKey = 'evolution' | 'content';

const PERIODS: { key: PeriodKey; label: string; days: number }[] = [
    { key: '7', label: '7 dias', days: 7 },
    { key: '30', label: '30 dias', days: 30 },
    { key: '90', label: '90 dias', days: 90 },
    { key: 'all', label: 'Tudo', days: 9999 },
];

const Spinner: React.FC<{ size?: number }> = ({ size = 32 }) => (
    <div className="qst-spinner" style={{ width: size, height: size }} />
);

const TrendBadge: React.FC<{ trend: Trend; invertColors?: boolean }> = ({ trend, invertColors = false }) => {
    if (trend.direction === 'neutral' || trend.value === 0) return null;
    const isPositive = invertColors ? trend.direction === 'down' : trend.direction === 'up';
    return (
        <span className={`qst-trend-badge ${isPositive ? 'positive' : 'negative'}`}>
            {trend.direction === 'up' ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {trend.value}%
        </span>
    );
};

// ─── Gráfico de Curva de Evolução ─────────────────────────────────────────────
const EvolutionChart: React.FC<{ days: DayData[]; metric: 'accuracy' | 'total' }> = ({ days, metric }) => {
    const [animated, setAnimated] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const W = 900, H = 240, PAD = { top: 20, right: 24, bottom: 44, left: 44 };
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    useEffect(() => {
        setAnimated(false);
        const t = setTimeout(() => setAnimated(true), 100);
        return () => clearTimeout(t);
    }, [days, metric]);

    const values = days.map(d => metric === 'accuracy' ? (d.accuracy ?? 0) : d.total);
    const activeDays = days.filter((_, i) => values[i] > 0);

    // Trim to actual data range so active points aren't compressed to one edge
    const firstActive = values.findIndex(v => v > 0);
    const lastActive = firstActive >= 0
        ? values.length - 1 - [...values].reverse().findIndex(v => v > 0)
        : -1;
    const displayDays = firstActive >= 0 ? days.slice(firstActive, lastActive + 1) : days;
    const displayValues = firstActive >= 0 ? values.slice(firstActive, lastActive + 1) : values;
    const maxVal = Math.max(...displayValues, metric === 'accuracy' ? 100 : 1);

    const x = (i: number) => PAD.left + (displayDays.length > 1 ? (i / (displayDays.length - 1)) * innerW : innerW / 2);
    const y = (v: number) => PAD.top + innerH - (v / maxVal) * innerH;

    const points = displayDays.map((d, i) => ({ x: x(i), y: y(displayValues[i]), val: displayValues[i], label: d.label }));
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaD = `${pathD} L ${points[points.length - 1].x} ${PAD.top + innerH} L ${points[0].x} ${PAD.top + innerH} Z`;

    const gridLines = metric === 'accuracy'
        ? [0, 25, 50, 75, 100]
        : Array.from({ length: 5 }, (_, i) => Math.round((i / 4) * maxVal));

    const totalDays = displayDays.length;
    let labelStep: number;
    if (totalDays <= 14) labelStep = 1;
    else if (totalDays <= 30) labelStep = 3;
    else if (totalDays <= 90) labelStep = 7;
    else if (totalDays <= 180) labelStep = 14;
    else if (totalDays <= 365) labelStep = 30;
    else labelStep = Math.ceil(totalDays / 12);

    if (activeDays.length === 0) {
        return (
            <div className="qst-chart-empty">
                <Activity size={32} opacity={0.3} />
                <p>Nenhum dado no período</p>
            </div>
        );
    }

    const TW = 72, TH = 34;

    return (
        <div className="qst-evolution-chart">
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="qst-svg">
                <defs>
                    <linearGradient id="qst-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--btn-primary)" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="var(--btn-primary)" stopOpacity="0" />
                    </linearGradient>
                    <clipPath id="qst-clip">
                        <rect x={PAD.left} y={PAD.top} width={innerW} height={innerH} />
                    </clipPath>
                </defs>

                {gridLines.map(v => (
                    <g key={v}>
                        <line x1={PAD.left} x2={PAD.left + innerW} y1={y(v)} y2={y(v)}
                            stroke="var(--platform-a10)" strokeWidth={1} />
                        <text x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle"
                            className="qst-axis-label">
                            {metric === 'accuracy' ? `${v}%` : v}
                        </text>
                    </g>
                ))}

                {displayDays.map((d, i) => {
                    const isLast = i === displayDays.length - 1;
                    const shouldShow = i % labelStep === 0 || isLast;
                    const tooClose = isLast && (displayDays.length - 1) % labelStep !== 0
                        && (displayDays.length - 1 - Math.floor((displayDays.length - 1) / labelStep) * labelStep) < labelStep * 0.4;
                    if (!shouldShow || tooClose) return null;
                    return (
                        <text key={i} x={x(i)} y={H - 6} textAnchor="middle" className="qst-axis-label">
                            {d.label}
                        </text>
                    );
                })}

                <path d={areaD} fill="url(#qst-grad)" clipPath="url(#qst-clip)"
                    className={animated ? 'qst-area-animated' : 'qst-area-hidden'} />

                <path d={pathD} fill="none" stroke="var(--btn-primary)" strokeWidth={2.5}
                    strokeLinecap="round" strokeLinejoin="round"
                    clipPath="url(#qst-clip)"
                    className={animated ? 'qst-line-animated' : 'qst-line-hidden'}
                    pathLength={1}
                />

                {points.map((p, i) => {
                    if (p.val <= 0) return null;
                    let tx = p.x;
                    let ty = p.y - 42;
                    if (ty < PAD.top + 2) ty = p.y + 14;
                    const half = TW / 2;
                    if (tx - half < PAD.left) tx = PAD.left + half;
                    if (tx + half > PAD.left + innerW) tx = PAD.left + innerW - half;

                    return (
                        <g key={i} className="qst-chart-point-group">
                            <circle cx={p.x} cy={p.y} r={4} fill="var(--btn-primary)"
                                className="qst-chart-point" />
                            <circle cx={p.x} cy={p.y} r={10} fill="transparent"
                                className="qst-chart-point-hit" />
                            <g className="qst-tooltip" transform={`translate(${tx},${ty})`}>
                                <rect x={-TW / 2} y={0} width={TW} height={TH} rx={5}
                                    fill="var(--btn-primary)" />
                                <text textAnchor="middle" y={13} className="qst-tooltip-text">
                                    {metric === 'accuracy' ? `${p.val}%` : `${p.val}q`}
                                </text>
                                <text textAnchor="middle" y={27} className="qst-tooltip-date">
                                    {p.label}
                                </text>
                            </g>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// ─── Barras de Dificuldade ────────────────────────────────────────────────────
const DifficultyBars: React.FC<{ distribution: DiffEntry[]; globalAvg: number }> = ({ distribution, globalAvg }) => {
    const [widths, setWidths] = useState<number[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const firedRef = useRef(false);

    useEffect(() => {
        // Reset ao trocar dados
        firedRef.current = false;
        setWidths(distribution.map(() => 0));

        const el = containerRef.current;
        if (!el) return;

        observerRef.current?.disconnect();
        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting || firedRef.current) return;
                firedRef.current = true;
                observerRef.current?.disconnect();

                // Duplo rAF: garante que o browser pintou o estado 0 antes de animar
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setWidths(distribution.map(d => d.accuracy));
                    });
                });
            },
            { threshold: 0.15 }
        );
        observerRef.current.observe(el);

        return () => observerRef.current?.disconnect();
    }, [distribution]);

    const colors: Record<string, string> = {
        facil: '#10b981', medio: '#f59e0b', dificil: '#ef4444'
    };

    return (
        <div className="qst-diff-bars" ref={containerRef}>
            {distribution.map((d, i) => (
                <div key={d.level} className="qst-diff-bar-row" style={{ animationDelay: `${i * 120}ms` }}>
                    <div className="qst-diff-bar-header">
                        <span className="qst-diff-label" style={{ color: colors[d.level] }}>{d.label}</span>
                        <div className="qst-diff-meta">
                            <span className="qst-diff-count">{d.total} questões</span>
                            {d.vsAverage !== 0 && (
                                <span className={`qst-diff-vs ${d.vsAverage > 0 ? 'pos' : 'neg'}`}>
                                    {d.vsAverage > 0 ? '+' : ''}{d.vsAverage}% vs média
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="qst-diff-track">
                        <div
                            className="qst-diff-fill"
                            style={{
                                width: `${widths[i] ?? 0}%`,
                                background: colors[d.level],
                                transitionDelay: `${i * 180}ms`,
                            }}
                        >
                            <span className="qst-diff-pct">{d.accuracy}%</span>
                        </div>
                    </div>
                    <div className="qst-diff-sub">
                        <div className="qst-diff-sub-group">
                            <span className="qst-diff-sub-dot correct" />
                            <span>{d.correct} certas</span>
                        </div>
                        <div className="qst-diff-sub-group">
                            <span className="qst-diff-sub-dot wrong" />
                            <span>{d.total - d.correct} erradas</span>
                        </div>
                    </div>
                </div>
            ))}
            <div className="qst-diff-avg-line">
                <Minus size={14} />
                <span>Média geral: <strong>{globalAvg}%</strong></span>
            </div>
        </div>
    );
};

// ─── Nó da Árvore Hierárquica ─────────────────────────────────────────────────
const PerfTreeNode: React.FC<{ node: PerfNode; depth?: number }> = ({ node, depth = 0 }) => {
    const [open, setOpen] = useState(depth === 0);
    const hasChildren = node.children && node.children.length > 0;
    const acc = node.accuracy;
    const accColor = acc >= 70 ? '#2F7130' : acc >= 50 ? '#f59e0b' : '#ef4444';

    return (
        <div className={`qst-tree-node depth-${depth}`}>
            <div
                className={`qst-tree-row ${hasChildren ? 'clickable' : ''} ${open ? 'open' : ''}`}
                onClick={() => hasChildren && setOpen(p => !p)}
            >
                <div className="qst-tree-left">
                    {hasChildren
                        ? (open ? <ChevronDown size={14} className="qst-tree-chevron" /> : <ChevronRight size={14} className="qst-tree-chevron" />)
                        : <span className="qst-tree-leaf-dot" />
                    }
                    <span className="qst-tree-name">{node.name}</span>
                </div>
                <div className="qst-tree-stats">
                    <div className="qst-tree-mini-bar">
                        <div className="qst-tree-mini-fill"
                            style={{ width: `${acc}%`, background: accColor }} />
                    </div>
                    <span className="qst-tree-acc" style={{ color: accColor }}>{acc}%</span>
                    <span className="qst-tree-total">{node.total}q</span>
                    <span className="qst-tree-time">{node.avgTimeFormatted}</span>
                    <div className="qst-tree-diffs">
                        {node.byDiff.map(d => {
                            const c = d.level === 'facil' ? '#10b981' : d.level === 'dificil' ? '#ef4444' : '#f59e0b';
                            return (
                                <span key={d.level} className="qst-tree-diff-chip" style={{ borderColor: c, color: c }}>
                                    {d.label[0]} {d.accuracy}%
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
            {open && hasChildren && (
                <div className="qst-tree-children">
                    {node.children.map(child => (
                        <PerfTreeNode key={child.name} node={child} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Heatmap de Performance ───────────────────────────────────────────────────
const PerfHeatmap: React.FC<{ nodes: PerfNode[] }> = ({ nodes }) => {
    const MAX_SHOW = 20;
    const flat: { name: string; acc: number; total: number }[] = [];
    const collect = (node: PerfNode) => {
        flat.push({ name: node.name, acc: node.accuracy, total: node.total });
        node.children?.forEach(collect);
    };
    nodes.forEach(collect);
    const sorted = flat.filter(n => n.total >= 3).sort((a, b) => b.total - a.total).slice(0, MAX_SHOW);
    if (sorted.length === 0) return null;

    return (
        <div className="qst-heatmap">
            {sorted.map((item, i) => {
                const color = item.acc >= 70
                    ? `rgba(47,113,48,${0.3 + (item.acc / 100) * 0.7})`
                    : item.acc >= 50
                        ? `rgba(245,158,11,${0.3 + (item.acc / 100) * 0.7})`
                        : `rgba(239,68,68,${0.3 + ((100 - item.acc) / 100) * 0.7})`;
                return (
                    <div key={i} className="qst-heatmap-cell" style={{ background: color }}
                        title={`${item.name}: ${item.acc}% de acerto (${item.total} questões)`}>
                        <span className="qst-heatmap-name">{item.name}</span>
                        <span className="qst-heatmap-pct">{item.acc}%</span>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Barras de Fragilidade ────────────────────────────────────────────────────
const FragilityChart: React.FC<{ items: FragilityItem[] }> = ({ items }) => {
    const [animated, setAnimated] = useState(false);
    useEffect(() => {
        setAnimated(false);
        const t = setTimeout(() => setAnimated(true), 200);
        return () => clearTimeout(t);
    }, [items]);

    if (!items.length) return (
        <div className="qst-chart-empty">
            <CheckCircle size={32} opacity={0.3} />
            <p>Nenhuma área crítica identificada</p>
        </div>
    );

    const maxScore = Math.max(...items.map(i => i.fragilityScore), 1);

    return (
        <div className="qst-fragility-list">
            {items.map((item, i) => (
                <div key={i} className="qst-fragility-item" style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="qst-fragility-header">
                        <div className="qst-fragility-rank">#{i + 1}</div>
                        <div className="qst-fragility-info">
                            <span className="qst-fragility-name">{item.name}</span>
                            {item.parent && <span className="qst-fragility-parent">{item.parent}</span>}
                        </div>
                        <div className="qst-fragility-nums">
                            <span className="qst-fragility-rate">{item.errorRate}% erro</span>
                            <span className="qst-fragility-count">{item.errors}/{item.total}</span>
                        </div>
                    </div>
                    <div className="qst-fragility-track">
                        <div className="qst-fragility-fill"
                            style={{
                                width: animated ? `${(item.fragilityScore / maxScore) * 100}%` : '0%',
                                transitionDelay: `${i * 80}ms`
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

// ─── Card de Overview ─────────────────────────────────────────────────────────
const OverviewCard: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string | number;
    detail?: string;
    trend?: Trend;
    invertTrend?: boolean;
    highlight?: boolean;
    className?: string;
}> = ({ icon: Icon, label, value, detail, trend, invertTrend, highlight, className = '' }) => (
    <div className={`qst-ov-card ${highlight ? 'highlight' : ''} ${className}`}>
        <Icon className="qst-ov-icon" size={28} />
        <div className="qst-ov-body">
            <div className="qst-ov-value">{value}</div>
            <div className="qst-ov-label">{label}</div>
            {detail && <div className="qst-ov-detail">{detail}</div>}
            {trend && <TrendBadge trend={trend} invertColors={invertTrend} />}
        </div>
    </div>
);

const PeriodSelector: React.FC<{ active: PeriodKey; onChange: (k: PeriodKey) => void }> = ({ active, onChange }) => (
    <div className="qst-period-selector">
        {PERIODS.map(p => (
            <button key={p.key}
                className={`qst-period-btn ${active === p.key ? 'active' : ''}`}
                onClick={() => onChange(p.key)}>
                {p.label}
            </button>
        ))}
    </div>
);

const MetricToggle: React.FC<{ active: 'accuracy' | 'total'; onChange: (m: 'accuracy' | 'total') => void }> = ({ active, onChange }) => (
    <div className="qst-metric-toggle">
        <button className={active === 'accuracy' ? 'active' : ''} onClick={() => onChange('accuracy')}>
            <Target size={13} /> Precisão
        </button>
        <button className={active === 'total' ? 'active' : ''} onClick={() => onChange('total')}>
            <BarChart2 size={13} /> Volume
        </button>
    </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
const QuestionStatistics: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabKey>('evolution');
    const [period, setPeriod] = useState<PeriodKey>('30');
    const [overview, setOverview] = useState<Overview | null>(null);
    const [evolutionDays, setEvolutionDays] = useState<DayData[]>([]);
    const [diffDist, setDiffDist] = useState<DiffEntry[]>([]);
    const [globalAvg, setGlobalAvg] = useState(0);
    const [errorAnalysis, setErrorAnalysis] = useState<ErrorAnalysis | null>(null);
    const [performance, setPerformance] = useState<PerfNode[]>([]);
    const [fragility, setFragility] = useState<FragilityItem[]>([]);
    const [loadingEvolution, setLoadingEvolution] = useState(false);
    const [loadingContent, setLoadingContent] = useState(false);
    const [chartMetric, setChartMetric] = useState<'accuracy' | 'total'>('accuracy');
    const [contentPeriod, setContentPeriod] = useState<PeriodKey>('all');

    // ✅ FIX: AbortController refs — cancela requisições ao desmontar ou trocar período
    const evolutionAbortRef = useRef<AbortController | null>(null);
    const contentAbortRef = useRef<AbortController | null>(null);

    // ✅ FIX: Guards contra chamadas simultâneas
    const evolutionLoadingRef = useRef(false);
    const contentLoadingRef = useRef(false);

    // ✅ FIX: loadEvolution sem useCallback — usa refs para controle
    const loadEvolution = async (abortSignal?: AbortSignal) => {
        // Guard: evita múltiplas requisições simultâneas
        if (evolutionLoadingRef.current) return;
        evolutionLoadingRef.current = true;
        setLoadingEvolution(true);

        try {
            const d = PERIODS.find(p => p.key === period)?.days || 30;
            const [ov, curve, diff, err] = await Promise.all([
                apiFetch(`/overview?period=${d}`, abortSignal),
                apiFetch(`/evolution-curve?period=${d}`, abortSignal),
                apiFetch(`/difficulty-distribution?period=${d}`, abortSignal),
                apiFetch(`/error-analysis?period=${d}`, abortSignal)
            ]);
            if (ov.success) setOverview(ov.overview);
            if (curve.success) setEvolutionDays(curve.days);
            if (diff.success) { setDiffDist(diff.distribution); setGlobalAvg(diff.globalAvg); }
            if (err.success) setErrorAnalysis(err.errorAnalysis);
        } catch (e: any) {
            // Ignora erro de abort (navegação/desmontagem)
            if (e?.name !== 'AbortError') {
                console.error('❌ [QSTATS] loadEvolution:', e);
            }
        } finally {
            evolutionLoadingRef.current = false;
            setLoadingEvolution(false);
        }
    };

    // ✅ FIX: loadContent sem useCallback — usa refs para controle
    const loadContent = async (abortSignal?: AbortSignal) => {
        if (contentLoadingRef.current) return;
        contentLoadingRef.current = true;
        setLoadingContent(true);

        try {
            const cd = PERIODS.find(p => p.key === contentPeriod)?.days || 9999;
            const [perf, frag] = await Promise.all([
                apiFetch(`/content-performance?period=${cd}`, abortSignal),
                apiFetch('/fragility-index', abortSignal)
            ]);
            if (perf.success) setPerformance(perf.performance);
            if (frag.success) setFragility(frag.fragility);
        } catch (e: any) {
            if (e?.name !== 'AbortError') {
                console.error('❌ [QSTATS] loadContent:', e);
            }
        } finally {
            contentLoadingRef.current = false;
            setLoadingContent(false);
        }
    };

    // ✅ FIX: Effect de evolução com cleanup correto
    useEffect(() => {
        if (activeTab !== 'evolution') return;

        // Cancela qualquer requisição anterior
        if (evolutionAbortRef.current) {
            evolutionAbortRef.current.abort();
        }
        evolutionAbortRef.current = new AbortController();
        evolutionLoadingRef.current = false; // reseta guard após abort

        loadEvolution(evolutionAbortRef.current.signal);

        return () => {
            // Cleanup ao desmontar ou trocar dependência
            if (evolutionAbortRef.current) {
                evolutionAbortRef.current.abort();
                evolutionAbortRef.current = null;
            }
            evolutionLoadingRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, period]);

    // ✅ FIX: Effect de conteúdo com cleanup correto
    useEffect(() => {
        if (activeTab !== 'content') return;

        if (contentAbortRef.current) {
            contentAbortRef.current.abort();
        }
        contentAbortRef.current = new AbortController();
        contentLoadingRef.current = false;

        loadContent(contentAbortRef.current.signal);

        return () => {
            if (contentAbortRef.current) {
                contentAbortRef.current.abort();
                contentAbortRef.current = null;
            }
            contentLoadingRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, contentPeriod]);

    // ✅ FIX: Cleanup geral ao desmontar o componente
    useEffect(() => {
        return () => {
            evolutionAbortRef.current?.abort();
            contentAbortRef.current?.abort();
            evolutionLoadingRef.current = false;
            contentLoadingRef.current = false;
        };
    }, []);

    // Handlers de refresh manual — criam novo AbortController
    const handleRefreshEvolution = () => {
        if (evolutionAbortRef.current) evolutionAbortRef.current.abort();
        evolutionAbortRef.current = new AbortController();
        evolutionLoadingRef.current = false;
        loadEvolution(evolutionAbortRef.current.signal);
    };

    const handleRefreshContent = () => {
        if (contentAbortRef.current) contentAbortRef.current.abort();
        contentAbortRef.current = new AbortController();
        contentLoadingRef.current = false;
        loadContent(contentAbortRef.current.signal);
    };

    const renderEvolution = () => (
        <div className="qst-tab-body">
            <div className="qst-section-header">
                <PeriodSelector active={period} onChange={setPeriod} />
                <button className="qst-refresh-btn" onClick={handleRefreshEvolution} disabled={loadingEvolution}>
                    <RefreshCw size={14} className={loadingEvolution ? 'spinning' : ''} />
                </button>
            </div>

            {loadingEvolution && !overview ? (
                <div className="qst-loading"><Spinner size={40} /><p>Carregando estatísticas...</p></div>
            ) : (
                <>
                    <section className="qst-section">
                        <h3 className="qst-section-title">
                            <Target size={16} /> Visão Geral
                            <span className="qst-section-period">
                                {PERIODS.find(p => p.key === period)?.label}{' vs período anterior'}
                            </span>
                        </h3>
                        <div className="qst-ov-grid">
                            <OverviewCard icon={BookOpen} highlight label="Questões resolvidas"
                                value={overview?.totalResolved ?? '—'} detail="No período selecionado"
                                trend={overview?.trends.total} />
                            <OverviewCard icon={Target} label="Taxa de acerto"
                                value={overview ? `${overview.accuracy}%` : '—'}
                                trend={overview?.trends.accuracy} />
                            <OverviewCard icon={Zap} label="1ª tentativa correta"
                                value={overview ? `${overview.firstAttemptPct}%` : '—'}
                                detail="Acertou sem repetir" />
                            <OverviewCard icon={Clock} label="Tempo médio"
                                value={overview?.avgTimeFormatted ?? '—'}
                                trend={overview?.trends.avgTime} invertTrend />
                        </div>
                    </section>

                    <section className="qst-section">
                        <div className="qst-section-header-row">
                            <h3 className="qst-section-title"><TrendingUp size={16} /> Curva de Evolução</h3>
                            <MetricToggle active={chartMetric} onChange={setChartMetric} />
                        </div>
                        <div className="qst-chart-card">
                            {evolutionDays.length > 0
                                ? <EvolutionChart days={evolutionDays} metric={chartMetric} />
                                : <div className="qst-chart-empty"><Activity size={32} opacity={0.3} /><p>Nenhum dado</p></div>
                            }
                        </div>
                    </section>

                    <section className="qst-section">
                        <h3 className="qst-section-title"><Layers size={16} /> Distribuição por Dificuldade</h3>
                        <div className="qst-card">
                            {diffDist.length > 0
                                ? <DifficultyBars distribution={diffDist} globalAvg={globalAvg} />
                                : <div className="qst-chart-empty"><p>Nenhum dado</p></div>
                            }
                        </div>
                    </section>

                    <section className="qst-section">
                        <h3 className="qst-section-title"><XCircle size={16} /> Análise de Erros</h3>
                        <div className="qst-ov-grid">
                            <OverviewCard
                                icon={AlertTriangle}
                                label="Erros recorrentes"
                                value={errorAnalysis?.recurrentErrorCount ?? 0}
                                detail="Errou 2+ vezes"
                            />
                            <OverviewCard
                                icon={XCircle}
                                label="Taxa de erro"
                                value={errorAnalysis ? `${errorAnalysis.errorRate}%` : '0%'}
                                detail="No período"
                                className="qst-error-card"
                            />
                            <OverviewCard
                                icon={Activity}
                                label="Recorrência"
                                value={errorAnalysis ? `${errorAnalysis.recurrentRate}%` : '0%'}
                                detail="Do total respondido"
                                className="qst-warn-card"
                            />
                            <OverviewCard
                                icon={Clock}
                                label="Tempo nos erros"
                                value={errorAnalysis?.avgTimeWrongFormatted ?? '—'}
                                detail="Média nas erradas"
                            />
                        </div>
                    </section>
                </>
            )}
        </div>
    );

    const renderContent = () => (
        <div className="qst-tab-body">
            <div className="qst-section-header">
                <PeriodSelector active={contentPeriod} onChange={setContentPeriod} />
                <button className="qst-refresh-btn" onClick={handleRefreshContent} disabled={loadingContent}>
                    <RefreshCw size={14} className={loadingContent ? 'spinning' : ''} />
                </button>
            </div>

            {loadingContent && !performance.length ? (
                <div className="qst-loading"><Spinner size={40} /><p>Carregando desempenho...</p></div>
            ) : (
                <>
                    <section className="qst-section">
                        <h3 className="qst-section-title">
                            <AlertTriangle size={16} /> Índice de Fragilidade
                            <span className="qst-section-badge danger">Áreas críticas</span>
                        </h3>
                        <div className="qst-card"><FragilityChart items={fragility} /></div>
                    </section>

                    {performance.length > 0 && (
                        <section className="qst-section">
                            <h3 className="qst-section-title"><Activity size={16} /> Mapa de Desempenho</h3>
                            <div className="qst-card"><PerfHeatmap nodes={performance} /></div>
                        </section>
                    )}

                    <section className="qst-section">
                        <h3 className="qst-section-title"><Layers size={16} /> Desempenho Hierárquico</h3>
                        {performance.length > 0 ? (
                            <div className="qst-tree-card">
                                <div className="qst-tree-header-row">
                                    <span className="qst-tree-header-name">Conteúdo</span>
                                    <div className="qst-tree-header-stats">
                                        <span className="qst-tree-header-bar"></span>
                                        <span className="qst-tree-header-acc">Precisão</span>
                                        <span className="qst-tree-header-total">Qtd</span>
                                        <span className="qst-tree-header-time">Tempo</span>
                                        <span className="qst-tree-header-diffs">Dificuldade</span>
                                    </div>
                                </div>
                                {performance.map(node => (
                                    <PerfTreeNode key={node.name} node={node} depth={0} />
                                ))}
                            </div>
                        ) : (
                            <div className="qst-chart-empty">
                                <BookOpen size={36} opacity={0.25} />
                                <p>Nenhuma questão respondida ainda</p>
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );

    return (
        <div className="qst-container">
            <div className="dsb-estx-view-selector">
                <button className={`dsb-estx-view-btn ${activeTab === 'evolution' ? 'active' : ''}`}
                    onClick={() => setActiveTab('evolution')}>
                    Evolução
                </button>
                <button className={`dsb-estx-view-btn ${activeTab === 'content' ? 'active' : ''}`}
                    onClick={() => setActiveTab('content')}>
                    Desempenho por Conteúdo
                </button>
            </div>

            {activeTab === 'evolution' && renderEvolution()}
            {activeTab === 'content' && renderContent()}
        </div>
    );
};

export default QuestionStatistics;